import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import './RouteCard.css';
import ElevationProfile from './ElevationProfile';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getAuthToken } from '../utils/auth';
import { formatTimeFromDate, formatNumericDate } from '../utils/timeFormat';
import RouvyAnalyzerModal from './RouvyAnalyzerModal';
import ExtraRaceModal from './ExtraRaceModal';

const RouteCard = ({ event_route, compact = false, onOpenSchedule, event = null, onExtraRacesChanged }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [isAdditionalInfoOpen, setIsAdditionalInfoOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadUrls, setDownloadUrls] = useState({ video: null, info: null });
  const [isWildCardModalOpen, setIsWildCardModalOpen] = useState(false);
  const [isAnalyzerOpen, setIsAnalyzerOpen] = useState(false);
  const [isExtraRaceOpen, setIsExtraRaceOpen] = useState(false);
  const [removingExtraRaceId, setRemovingExtraRaceId] = useState(null);
  const [wildCardDateTime, setWildCardDateTime] = useState('');
  const [wildCardUrl, setWildCardUrl] = useState('');
  const [wildCardSubmitting, setWildCardSubmitting] = useState(false);
  const [wildCardError, setWildCardError] = useState(null);
  const [wildCardSuccess, setWildCardSuccess] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0, width: 0 });
  // Пока превью маршрута грузится, его место держит заглушка того же размера —
  // иначе карточка подпрыгивает, когда картинка наконец приходит с CDN
  const [imageStatus, setImageStatus] = useState('loading');
  const imageRef = useRef(null);
  const cardRef = useRef(null);
  console.log(event_route)
  const route = event_route.route
  const parameters = route.parameters || {};
  const thumbnailUrl = parameters.thumbnail || route.image || '';
  const routeUrl = parameters.url || route.rouvy_link || '';
  const eventRoute = event_route || {};
  // Remove " | Race A" suffix from route name
  const rawRouteName = eventRoute.name || 'Unnamed Route';
  const routeName = rawRouteName.replace(/\s*\|\s*Race\s+[A-Z]$/i, '');
  const allTimes = eventRoute.all_times || [];
  const nextStartTime = eventRoute.next_start_time;
  // Маршрут открытого турнира: расписания проездов у него нет,
  // гонку в ROUVY участник создает себе сам
  const openStage = allTimes.length > 0 && allTimes.every(time => time.open_stage);
  const openWindow = openStage ? allTimes[0] : null;

  // Лимит гонок на этапе: сколько гонок разрешено проехать и что участник уже занял
  const racesPerStage = event?.races_per_stage || null;
  const userStage = eventRoute.user_stage || null;
  const registeredRacesCount = userStage?.registered_race_ids?.length || 0;
  // Все допустимые попытки проеханы — этап закрыт, регистрироваться нельзя
  const stageLocked = Boolean(racesPerStage && userStage?.locked);

  // Дополнительные заезды (RMC): участник сам создает в ROUVY гонку по маршруту
  // этапа в любой день недели этапа — результат идет в зачет этапа
  const stageWeek = eventRoute.stage_week || null;
  const stageExtraRaces = (event?.user_extra_races || []).filter(race => race.route_id === route.id);
  const canAddExtraRace = Boolean(user && event?.extra_races_allowed && stageWeek?.submission_open);

  // Check if all races on this route have passed
  const allRacesPassed = !openStage && allTimes.length > 0 && allTimes.every(time => {
    const raceTime = new Date(time.start_date_time_utc);
    return raceTime < new Date();
  });

  // Format date with user's timezone and language
  const formatEventDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      
      const time = formatTimeFromDate(date, language);
      const dayOfWeek = t('common.days')[date.getDay()];
      
      // Get timezone abbreviation
      const timeZone = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
      
      return `${dayOfWeek}, ${formatNumericDate(date, language)}, ${time} ${timeZone}`;
    } catch (e) {
      return null;
    }
  };

  // Окно проезда маршрута в открытом турнире.
  // Границ может не быть вовсе — тогда проехать можно когда угодно
  const openWindowText = () => {
    // Границы этапа, а если их нет — период проведения самого турнира
    const fromValue = openWindow?.start_date_time_utc || event?.starts_at;
    const untilValue = openWindow?.end_date_time_utc || event?.ends_at;
    const from = fromValue ? formatEventDate(fromValue) : null;
    const until = untilValue ? formatEventDate(untilValue) : null;

    if (!from && !until) return t('route_card.open_window_any_time');

    return [
      from && t('route_card.open_window_from').replace('{{from}}', from),
      until && t('route_card.open_window_until').replace('{{until}}', until)
    ].filter(Boolean).join(' ');
  };

  const getTimeUntil = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    
    const now = new Date();
    const target = new Date(dateTimeStr);
    const diffMs = target - now;
    
    if (diffMs < 0) return t('route_card.time_left.passed');
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffDays > 0) {
      return `${t('route_card.time_left.in')} ${diffDays}${t('route_card.time_left.days')} ${diffHours}${t('route_card.time_left.hours')}`;
    } else if (diffHours > 0) {
      return `${t('route_card.time_left.in')} ${diffHours}${t('route_card.time_left.hours')}`;
    } else if (diffMinutes > 0) {
      return `${t('route_card.time_left.in')} ${diffMinutes}${t('route_card.time_left.minutes')}`;
    } else {
      return t('route_card.time_left.soon');
    }
  };

  // Карточка целиком обернута в ссылку на ROUVY: клик по кнопке не должен
  // уводить на маршрут, прогноз открывается модальным окном
  const handleOpenAnalyzer = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnalyzerOpen(true);
  };

  const handleOpenSchedule = (e) => {
    e.preventDefault();
    if (onOpenSchedule && allTimes.length > 0) {
      onOpenSchedule(routeName, allTimes, event_route.route_id);
    }
  };

  const handleNextTimeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // При лимите гонок на этапе переход в Rouvy идет через расписание,
    // где можно зарегистрироваться на гонку
    if (racesPerStage) {
      handleOpenSchedule(e);
      return;
    }

    if (nextStartTime && allTimes.length > 0) {
      // Найти проезд с этим временем
      const nextTime = allTimes.find(time => time.start_date_time_utc === nextStartTime);
      if (nextTime && nextTime.rouvy_event_id) {
        const url = `https://riders.rouvy.com/events/${nextTime.rouvy_event_id}`;
        window.open(url, '_blank');
      }
    }
  };

  // Get accessibility icon
  const getAccessibilityIcon = (accessibility) => {
    switch (accessibility) {
      case 'private':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        );
      case 'public':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  // Get drafting icon
  const getDraftingIcon = () => {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/>
      </svg>
    );
  };

  // Превью могло прийти из кеша еще до того, как навесился onLoad — тогда
  // события уже не будет, и состояние надо снять с готового элемента
  useEffect(() => {
    setImageStatus('loading');

    const image = imageRef.current;
    if (image?.complete) {
      setImageStatus(image.naturalWidth > 0 ? 'loaded' : 'error');
    }
  }, [thumbnailUrl]);

  // Проверка статуса загрузки видео
  useEffect(() => {
    if (route.rouvy_id && !compact) {
      checkDownloadStatus();
    }
  }, [route.rouvy_id, compact]);

  const checkDownloadStatus = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/video_downloads/status/${route.rouvy_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDownloadStatus(data.status);
        if (data.status === 'completed' && data.download_video_url && data.download_info_url) {
          setDownloadUrls({
            video: data.download_video_url,
            info: data.download_info_url
          });
        }
      }
    } catch (error) {
      console.error('Error checking download status:', error);
    }
  };

  const handleDownloadVideo = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!route.id || !route.rouvy_id) {
      setDownloadError(t('route_card.download.no_rouvy_id'));
      return;
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error(t('route_card.download.unauthorized'));
      }

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/routes/${route.id}/download_video`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('route_card.download.failed'));
      }

      const data = await response.json();
      setDownloadStatus('downloading');
      
      // Периодически проверяем статус загрузки
      const intervalId = setInterval(async () => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          const statusResponse = await fetch(
            `${apiUrl}/api/video_downloads/status/${route.rouvy_id}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setDownloadStatus(statusData.status);
            
            if (statusData.status === 'completed') {
              clearInterval(intervalId);
              setIsDownloading(false);
              if (statusData.download_video_url && statusData.download_info_url) {
                setDownloadUrls({
                  video: statusData.download_video_url,
                  info: statusData.download_info_url
                });
              }
            }
          }
        } catch (error) {
          console.error('Error checking status:', error);
        }
      }, 3000);

      // Останавливаем проверку через 10 минут
      setTimeout(() => {
        clearInterval(intervalId);
        setIsDownloading(false);
      }, 600000);

    } catch (error) {
      console.error('Error downloading video:', error);
      setDownloadError(error.message);
      setIsDownloading(false);
    }
  };

  const getDownloadButtonContent = () => {
    if (downloadStatus === 'completed') {
      return t('route_card.download.completed');
    }
    
    if (isDownloading || downloadStatus === 'downloading') {
      return t('route_card.download.downloading');
    }
    
    return t('route_card.download.button');
  };

  const fetchWithRetry = async (url, options, maxRetries = 3, isLargeFile = false) => {
    // Для больших файлов увеличиваем количество попыток и таймаут
    const retries = isLargeFile ? 5 : maxRetries;
    
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // Добавляем таймаут для fetch
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), isLargeFile ? 120000 : 30000);
        
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        const isNetworkError = error.message.includes('network') || 
                              error.message.includes('ERR_NETWORK_CHANGED') ||
                              error.message.includes('aborted') ||
                              error.name === 'TypeError' ||
                              error.name === 'AbortError';
        
        if (attempt < retries - 1 && isNetworkError) {
          // Экспоненциальная задержка с увеличенным временем для больших файлов
          const baseDelay = isLargeFile ? 2000 : 1000;
          const delay = Math.pow(2, attempt) * baseDelay;
          console.log(`Retry attempt ${attempt + 1}/${retries} after ${delay}ms for ${url}`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }
  };

  const handleDownloadFile = async (e, fileType) => {
    e.preventDefault();
    e.stopPropagation();

    const token = getAuthToken();
    if (!token) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const url = fileType === 'video' ? downloadUrls.video : downloadUrls.info;
      
      // Добавляем токен в URL
      const urlWithToken = `${url}?token=${encodeURIComponent(token)}`;
      
      // Для видео используем флаг isLargeFile
      const isVideo = fileType === 'video';
      const response = await fetchWithRetry(`${apiUrl}${urlWithToken}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }, 3, isVideo);

      if (!response.ok) {
        throw new Error(t('route_card.download.failed'));
      }

      // Создаем blob из ответа
      const blob = await response.blob();
      
      // Создаем ссылку для скачивания
      const downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = fileType === 'video' 
        ? `video.mp4` 
        : `info.json`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      window.URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      console.error('Error downloading file:', error);
      setDownloadError(error.message);
    }
  };

  const handleDirectDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!route.rouvy_id) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setDownloadError(t('route_card.download.unauthorized'));
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const url = `${apiUrl}/api/video_downloads/direct/${route.rouvy_id}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(t('route_card.download.failed'));
      }

      // Создаем blob из ответа
      const blob = await response.blob();
      
      // Создаем ссылку для скачивания
      const downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = `${route.rouvy_id}_video.mp4`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      window.URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      console.error('Error downloading file:', error);
      setDownloadError(error.message);
    }
  };

  const handleWildCardOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setModalPosition({
        top: rect.top + rect.height / 2,
        left: rect.left + rect.width / 2,
        width: rect.width - 32,
      });
    }
    setWildCardError(null);
    setWildCardSuccess(false);
    setIsWildCardModalOpen(true);
  };

  const handleWildCardClose = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setIsWildCardModalOpen(false);
    setWildCardDateTime('');
    setWildCardUrl('');
    setWildCardError(null);
    setWildCardSuccess(false);
  };

  const handleWildCardSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const isOrganizer = event?.creation_type === 'organizer_created';
    if (isOrganizer && !wildCardDateTime) {
      setWildCardError(t('route_card.wild_card.error_datetime'));
      return;
    }
    if (!isOrganizer && !wildCardUrl.trim()) {
      setWildCardError(t('route_card.wild_card.error_url'));
      return;
    }
    setWildCardSubmitting(true);
    setWildCardError(null);
    try {
      const token = getAuthToken();
      const body = {
        wild_card: {
          route_id: route.id,
          event_id: event?.id,
          ...(isOrganizer
            ? { ride_time: new Date(wildCardDateTime).toISOString() }
            : { event_link: wildCardUrl.trim() })
        }
      };
      const response = await fetch('/api/wild_cards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        setWildCardSuccess(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setWildCardError(data.error || t('route_card.wild_card.error_submit'));
      }
    } catch {
      setWildCardError(t('route_card.wild_card.error_submit'));
    } finally {
      setWildCardSubmitting(false);
    }
  };

  // Карточка целиком ссылка на маршрут ROUVY — кнопки внутри не должны уводить туда
  const handleOpenExtraRace = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExtraRaceOpen(true);
  };

  const handleExtraRaceLinkClick = (e, link) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleRemoveExtraRace = async (e, extraRace) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(t('route_card.extra_race.remove_confirm'))) return;

    setRemovingExtraRaceId(extraRace.id);
    try {
      await axios.delete(`/api/events/${event.id}/extra_races/${extraRace.id}`);
      if (onExtraRacesChanged) onExtraRacesChanged();
    } catch (err) {
      alert(err.response?.data?.error || t('route_card.extra_race.error_remove'));
    } finally {
      setRemovingExtraRaceId(null);
    }
  };

  if (compact) {
    // Компактный вид - только иконка с ссылкой
    return (
      <a
        href={routeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`route-card-compact ${allRacesPassed ? 'all-races-passed' : ''}`}
        title={routeName}
      >
        {thumbnailUrl && imageStatus !== 'error' && (
          <img
            src={thumbnailUrl}
            alt={routeName}
            ref={imageRef}
            className="route-thumbnail"
            style={{ display: imageStatus === 'loaded' ? 'block' : 'none' }}
            onLoad={() => setImageStatus('loaded')}
            onError={() => setImageStatus('error')}
          />
        )}
        {(!thumbnailUrl || imageStatus !== 'loaded') && (
          <div className="route-thumbnail-placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
          </div>
        )}
      </a>
    );
  }

  // Полный вид - карточка с подробной информацией
  const card = (
    <a
      ref={cardRef}
      href={routeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="route-card-full"
    >
      <div className="route-card-image-link">
        {thumbnailUrl && imageStatus !== 'error' && (
          <img
            src={thumbnailUrl}
            alt={routeName}
            ref={imageRef}
            className="route-card-image"
            style={{ display: imageStatus === 'loaded' ? 'block' : 'none' }}
            onLoad={() => setImageStatus('loaded')}
            onError={() => setImageStatus('error')}
          />
        )}
        {(!thumbnailUrl || imageStatus !== 'loaded') && (
          <div className="route-card-image-placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
          </div>
        )}
        {/* Бейдж с названием маршрута */}
        {route.name && (
          <div className="route-name-badge">
            {route.name}
          </div>
        )}
        
        {/* Публичность и драфтинг приходят от гонки ROUVY: у маршрута открытого
            турнира гонки нет, поэтому и бейджа в углу превью не будет */}
        {!openStage && allTimes.length > 0 && (allTimes[0].accessibility || allTimes[0].drafting != null) && (
          <div className="route-image-badge">
            <div className="route-event-badges">
              {allTimes[0].accessibility && (
                <div
                  className={`route-badge route-badge-accessibility route-badge-${allTimes[0].accessibility}`}
                  title={t(`route_card.accessibility.${allTimes[0].accessibility}`)}
                >
                  {getAccessibilityIcon(allTimes[0].accessibility)}
                </div>
              )}
              {allTimes[0].drafting != null && (
                <div
                  className={`route-badge route-badge-drafting ${allTimes[0].drafting ? 'route-badge-drafting-enabled' : 'route-badge-drafting-disabled'}`}
                  title={t(`route_card.drafting.${allTimes[0].drafting ? 'enabled' : 'disabled'}`)}
                >
                  {getDraftingIcon()}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Бейджи скачивания готовых файлов */}
        {downloadStatus === 'completed' && downloadUrls.info && user?.admin && (
          <div className="route-download-badges">
            <div className="route-download-badges-container">
              <div
                className="route-badge route-badge-download route-badge-json"
                onClick={(e) => handleDownloadFile(e, 'info')}
                title={t('route_card.download.info_file')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </div>
            </div>
          </div>
        )}
        {event?.wild_cards > 0 && user && event.wild_cards_used < event.wild_cards && (
          <button
            className="route-wildcard-badge"
            onClick={handleWildCardOpen}
            title={event.creation_type === 'organizer_created'
              ? t('route_card.wild_card.request')
              : t('route_card.wild_card.provide')}
          >
            🃏
          </button>
        )}
      </div>

      <div className={`route-card-content ${allRacesPassed ? 'all-races-passed' : ''} ${stageLocked ? 'stage-locked' : ''}`}>
        <div className="route-card-header">
          <h3 className="route-card-title">{routeName || eventRoute.name}</h3>
          {racesPerStage > 0 && user && (
            <div className={`route-race-limit ${stageLocked ? 'route-race-limit-locked' : ''}`}>
              {stageLocked
                ? t('route_card.race_limit.stage_locked_short')
                : t('route_card.race_limit.card_summary')
                    .replace('{{registered}}', registeredRacesCount)
                    .replace('{{count}}', racesPerStage)}
            </div>
          )}
          <div className="route-card-actions">
            {allTimes.length > 0 && !openStage && (
              <button 
                className="route-schedule-button"
                onClick={handleOpenSchedule}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                {t('route_card.view_schedule')} ({allTimes.length})
              </button>
            )}
            {/* Прогноз времени и подбор снаряжения от стороннего сервиса
                по этому маршруту — см. RouvyAnalyzerModal */}
            {route.rouvy_id && (
              <button
                className="route-analyzer-button"
                onClick={handleOpenAnalyzer}
                title={t('route_card.analyzer_hint')}
              >
                {t('route_card.analyzer')}
              </button>
            )}
            {canAddExtraRace && (
              <button
                className="route-extra-race-button"
                onClick={handleOpenExtraRace}
                title={t('route_card.extra_race.add_hint')}
              >
                {t('route_card.extra_race.add')}
              </button>
            )}
          </div>
          {openStage && (
            <div className="route-open-window-box">
              <p className="route-next-time-label">{t('route_card.open_window')}:</p>
              <p className="route-next-time-date">{openWindowText()}</p>
              <p className="route-open-window-hint">{t('route_card.open_window_hint')}</p>
            </div>
          )}

          {!openStage && nextStartTime && (
            <div
              className={`route-next-time-box ${stageLocked ? 'route-next-time-box-locked' : ''}`}
              onClick={handleNextTimeClick}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleNextTimeClick(e);
                }
              }}
            >
              <p className="route-next-time-label">{t('route_card.next_ride')}:</p>
              <p className="route-next-time-date">
                {formatEventDate(nextStartTime)}
              </p>
              <p className="route-next-time-countdown">
                {getTimeUntil(nextStartTime)}
              </p>
            </div>
          )}
        </div>
        
        {stageExtraRaces.length > 0 && (
          <ul className="route-extra-races">
            {stageExtraRaces.map(extraRace => (
              <li key={extraRace.id} className="route-extra-race">
                <span className="route-extra-race-label">{t('route_card.extra_race.label')}</span>
                <button
                  type="button"
                  className="route-extra-race-link"
                  onClick={(e) => handleExtraRaceLinkClick(e, extraRace.event_link)}
                >
                  {extraRace.start_date_time_utc
                    ? formatEventDate(extraRace.start_date_time_utc)
                    : t('route_card.extra_race.open_race')}
                </button>
                {stageWeek?.submission_open && (
                  <button
                    type="button"
                    className="route-extra-race-remove"
                    onClick={(e) => handleRemoveExtraRace(e, extraRace)}
                    disabled={removingExtraRaceId === extraRace.id}
                    title={t('route_card.extra_race.remove')}
                    aria-label={t('route_card.extra_race.remove')}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {(parameters.description || route.rouvy_description) && (
          <p className="route-card-description">
            {parameters.description || route.rouvy_description}
          </p>
        )}
        
        {/* Скрываемый блок "Дополнительно" с профилем маршрута */}
        {(route.parameters?.simplifiedGeometryPoints || route.simplified_geometry_points || route.simplifiedGeometryPoints) && (
          <div className="route-additional-info">
            <button 
              className="route-additional-toggle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsAdditionalInfoOpen(!isAdditionalInfoOpen);
              }}
            >
              <span>{t('route_card.additional_info')}</span>
              <svg 
                className={`toggle-icon ${isAdditionalInfoOpen ? 'open' : ''}`}
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            
            {isAdditionalInfoOpen && (
              <div className="route-profile-section">
                <ElevationProfile 
                  geometryPoints={
                    route.parameters?.simplifiedGeometryPoints || 
                    route.simplified_geometry_points || 
                    route.simplifiedGeometryPoints
                  }
                  height={160}
                />
              </div>
            )}
          </div>
        )}
      </div>

    </a>
  );

  const wildCardModal = isWildCardModalOpen ? ReactDOM.createPortal(
    <div className="wildcard-modal-overlay" onClick={handleWildCardClose}>
      <div
        className="wildcard-modal"
        style={{ top: modalPosition.top, left: modalPosition.left, width: modalPosition.width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wildcard-modal-header">
          <h3>
            {event?.creation_type === 'organizer_created'
              ? t('route_card.wild_card.request_title')
              : t('route_card.wild_card.provide_title')}
          </h3>
          <button className="wildcard-modal-close" onClick={handleWildCardClose}>✕</button>
        </div>

        <div className="wildcard-modal-body">
          {wildCardSuccess ? (
            <p className="wildcard-success">{t('route_card.wild_card.success')}</p>
          ) : (
            <>
              {event?.creation_type === 'organizer_created' ? (
                <div className="wildcard-field">
                  <label>{t('route_card.wild_card.datetime_label')}</label>
                  <input
                    type="datetime-local"
                    value={wildCardDateTime}
                    onChange={(e) => setWildCardDateTime(e.target.value)}
                  />
                  <p className="wildcard-hint">{t('route_card.wild_card.datetime_hint')}</p>
                </div>
              ) : (
                <>
                  {event?.wild_card_description && (
                    <p className="wildcard-description">{event.wild_card_description}</p>
                  )}
                  <div className="wildcard-field">
                    <label>{t('route_card.wild_card.url_label')}</label>
                    <input
                      type="url"
                      value={wildCardUrl}
                      onChange={(e) => setWildCardUrl(e.target.value)}
                      placeholder="https://riders.rouvy.com/events/3b45c007-..."
                    />
                  </div>
                </>
              )}
              {wildCardError && <p className="wildcard-error">{wildCardError}</p>}
            </>
          )}
        </div>

        <div className="wildcard-modal-footer">
          {!wildCardSuccess && (
            <button
              className="wildcard-btn wildcard-btn-submit"
              onClick={handleWildCardSubmit}
              disabled={wildCardSubmitting}
            >
              {wildCardSubmitting ? t('common.saving') : t('route_card.wild_card.submit')}
            </button>
          )}
          <button className="wildcard-btn wildcard-btn-cancel" onClick={handleWildCardClose}>
            {wildCardSuccess ? t('common.close') : t('common.cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {card}
      {wildCardModal}
      <ExtraRaceModal
        isOpen={isExtraRaceOpen}
        onClose={() => setIsExtraRaceOpen(false)}
        eventId={event?.id}
        routeId={route.id}
        routeName={routeName}
        stageWeek={stageWeek}
        onAdded={onExtraRacesChanged}
      />
      <RouvyAnalyzerModal
        isOpen={isAnalyzerOpen}
        onClose={() => setIsAnalyzerOpen(false)}
        rouvyId={route.rouvy_id}
        routeName={routeName}
      />
    </>
  );
};

export default RouteCard;